import type { MatchState, PlayCardCommand } from "../../../../schemas"
import type { CommandResult } from "../state"
import {
  appendLog,
  applyDamageToInstance,
  err,
  getOtherSeatId,
  getSeat,
  moveInstance,
  resolveLethalDamage,
  updateInstance,
  updateSeat,
} from "../state"

export function handlePlayCard(
  state: MatchState,
  cmd: PlayCardCommand,
): CommandResult {
  const seat = getSeat(state, cmd.seatId)
  const { instanceId, destination, replaceSupportId } = cmd.payload

  // Must be in hand
  if (!seat.zones.hand.includes(instanceId)) {
    return err(
      "PLAY_CARD_NOT_IN_HAND",
      `Instance ${instanceId} is not in hand.`,
    )
  }

  const inst = state.instances[instanceId]
  if (!inst)
    return err("PLAY_CARD_UNKNOWN_INSTANCE", `Unknown instance ${instanceId}.`)

  // Validate destination capacity
  if (destination === "active") {
    if (seat.zones.active !== null) {
      return err(
        "PLAY_CARD_ACTIVE_OCCUPIED",
        "Active slot is occupied. Swap out the current unit first, or play to support.",
      )
    }
  } else {
    if (seat.zones.support.length >= state.config.supportCapacity) {
      if (!replaceSupportId) {
        return err(
          "PLAY_CARD_SUPPORT_FULL",
          `Support zone is full (max ${state.config.supportCapacity}).`,
        )
      }
      if (!seat.zones.support.includes(replaceSupportId)) {
        return err(
          "PLAY_CARD_REPLACE_NOT_IN_SUPPORT",
          `Support replacement ${replaceSupportId} is not in support.`,
        )
      }
    } else if (replaceSupportId) {
      return err(
        "PLAY_CARD_REPLACE_NOT_NEEDED",
        "Support replacement is only allowed when support is full.",
      )
    }
  }

  let s = state
  if (destination === "support" && replaceSupportId) {
    s = moveInstance(s, replaceSupportId, "discard", cmd.seatId)
  }

  s = moveInstance(s, instanceId, destination, cmd.seatId)

  // Mark as deployed this turn; active units can attack only if Swift
  const canAttack = destination === "active" && inst.combatState.isSwift
  s = updateInstance(s, instanceId, (i) => ({
    ...i,
    status: {
      ...i.status,
      deployedThisTurn: true,
      canAttackThisTurn: canAttack,
      hasAttackedThisTurn: false,
      remainingAttack: canAttack ? i.currentAttack : 0,
    },
  }))

  s = updateSeat(s, cmd.seatId, (seat) => ({
    ...seat,
    turn: { ...state.turn, actionCount: state.turn.actionCount + 1 },
  }))

  s = appendLog(s, cmd.seatId, "play_card", {
    instanceId,
    destination,
    replaceSupportId,
    cardId: inst.definitionId,
  })

  if (inst.combatState.hasDeploy) {
    const defenderSeatId = getOtherSeatId(cmd.seatId)
    const defenderActiveId = getSeat(s, defenderSeatId).zones.active
    if (defenderActiveId) {
      s = applyDamageToInstance(s, defenderActiveId, 1)
      s = appendLog(s, cmd.seatId, "deploy_ping", {
        instanceId,
        targetId: defenderActiveId,
        damage: 1,
      })
      s = resolveLethalDamage(s)
    }
  }

  if (inst.combatState.hasSpark) {
    s = updateSeat(s, cmd.seatId, (seat) => ({
      ...seat,
      resources: {
        ...seat.resources,
        currentEnergy: seat.resources.currentEnergy + 1,
      },
    }))
    s = appendLog(s, cmd.seatId, "spark_energy", { instanceId })
  }

  return {
    ok: true,
    state: { ...s, turn: { ...s.turn, actionCount: s.turn.actionCount + 1 } },
  }
}
